// =============================================================================
// DASHBOARD COMMERCIAL - Fiche Client Détaillée
// =============================================================================

import { useState, useEffect } from "react";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Building2,
  Thermometer,
  Edit,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  FolderPlus,
  FileText,
  Plus,
  MessageSquare,
  Send,
  Users,
  Euro,
  Home,
  Archive,
  ShieldCheck
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { OhCard, OhStatusNote, OhSelect } from "../../components/ui/oh-charte";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { CONTACT_RELATION_VALUES } from "../../utils/contactSecondaire";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { formatDateFR, formatDateTimeFR } from "../../utils/dateFormat";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useConfigList } from "../../hooks/useConfigList";
import { hasDossierSite } from "../../utils/tenantProfile";
import { SitesDuClient } from "./artisan/SitesDuClient";

// 🔴 N°838/840 — « personne morale ou non » = la règle UNIQUE du B2B
// (devisMode / core/client_type.is_b2b : professionnel, entreprise,
// copropriete, bailleur, pro). Avant : `["professionnel","entreprise"]` en dur à
// 10 endroits ⇒ un SYNDIC (copropriete) retombait dans le cas particulier et
// ⛔ ne voyait ni raison sociale, ni SIREN, ni SIRET.
// 🔑 P-MARIO-01 : la règle vit désormais dans `utils/clientFormat` (source unique).
import { formatClientFullName, formatClientAddress, estPersonneMorale, libelleTypeClient } from "../../utils/clientFormat";
import { AdresseAutocomplete } from "../../components/metier/AdresseAutocomplete";
import { normalizeName } from "../../lib/normalize";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// BARÈMES MPR 2024/2025 - Calcul de la couleur
// =============================================================================

const BAREMES_MPR = {
  // Île-de-France
  idf: {
    1: { bleu: 23541, jaune: 28657, violet: 40018 },
    2: { bleu: 34551, jaune: 42058, violet: 58827 },
    3: { bleu: 41493, jaune: 50513, violet: 70382 },
    4: { bleu: 48447, jaune: 58981, violet: 82839 },
    5: { bleu: 55427, jaune: 67473, violet: 94844 },
    supplement: { bleu: 6970, jaune: 8486, violet: 12006 }
  },
  // Province (hors Île-de-France)
  province: {
    1: { bleu: 17009, jaune: 21805, violet: 30549 },
    2: { bleu: 24875, jaune: 31889, violet: 44907 },
    3: { bleu: 29917, jaune: 38349, violet: 54071 },
    4: { bleu: 34948, jaune: 44802, violet: 63235 },
    5: { bleu: 40002, jaune: 51281, violet: 72400 },
    supplement: { bleu: 5045, jaune: 6462, violet: 9165 }
  }
};

function calculerCouleurMPR(rfr, nbPersonnes, region) {
  if (!rfr || !nbPersonnes) return null;
  
  const bareme = region === "idf" ? BAREMES_MPR.idf : BAREMES_MPR.province;
  
  // Calculer les seuils pour le nombre de personnes
  let seuils;
  if (nbPersonnes <= 5) {
    seuils = bareme[nbPersonnes];
  } else {
    // Au-delà de 5 personnes, ajouter le supplément
    const base = bareme[5];
    const supplement = bareme.supplement;
    const personnesSupp = nbPersonnes - 5;
    seuils = {
      bleu: base.bleu + (supplement.bleu * personnesSupp),
      jaune: base.jaune + (supplement.jaune * personnesSupp),
      violet: base.violet + (supplement.violet * personnesSupp)
    };
  }
  
  // Déterminer la couleur.
  // Charte : zéro couleur en dur → chaque catégorie MPR est portée par une PAIRE
  // dual-mode (fond teinté + texte foncé même famille), lisible en clair ET foncé.
  // La sémantique (bleu/jaune/violet/rose) reste portée par le LIBELLÉ, pas par
  // une teinte hardcodée (cf. gabarit DAL : couleur = accent/états, pas décor).
  const ohPair = (fam) => ({
    background: `var(--oh-${fam}-bg)`,
    color: `var(--oh-${fam}-text)`,
    borderColor: `var(--oh-${fam}-border)`,
  });
  const ohText = (fam) => ({ color: `var(--oh-${fam}-text)` });
  if (rfr <= seuils.bleu) {
    return { couleur: "bleu", label: "Très modestes", badgeStyle: ohPair("success"), labelStyle: ohText("success") };
  } else if (rfr <= seuils.jaune) {
    return { couleur: "jaune", label: "Modestes", badgeStyle: ohPair("info"), labelStyle: ohText("info") };
  } else if (rfr <= seuils.violet) {
    return { couleur: "violet", label: "Intermédiaires", badgeStyle: { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" }, labelStyle: { color: "var(--oh-accent-text)" } };
  } else {
    return { couleur: "rose", label: "Supérieurs", badgeStyle: ohPair("error"), labelStyle: ohText("error") };
  }
}

// Sentence case : première lettre en capitale, reste inchangé (charte §3).
const capitalizeFR = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function ClientDetail({ clientId, tenantId, onBack, onNavigate, onCreateDossier, currentUser }) {
  const { items: civiliteOptions } = useConfigList("civilites");
  const { items: occupationOptions } = useConfigList("statuts_occupation");
  // 🔴 N°840 — le LIBELLÉ du type vient de l'autorité tenant (N°797), ⛔ plus la
  // valeur brute (« copropriete »). Repli pour un tenant sans liste : l'affichage
  // d'avant, inchangé (omv_france / climatpac n'en ont pas — à traiter ensuite).
  const { items: typesClient } = useConfigList("client_types");
  const libelleType = (type) => libelleTypeClient(typesClient, type);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [commentaires, setCommentaires] = useState([]);
  const [activeTab, setActiveTab] = useState("infos");
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [showArchivedDossiers, setShowArchivedDossiers] = useState(false); // Masquer archivés par défaut
  const [syncingZoho, setSyncingZoho] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (clientId) {
      loadClient();
    }
  }, [clientId]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        ...(token && { "Authorization": `Bearer ${token}` })
      };

      // Charger tenant info
      const tenantRes = await fetch(`${API}/api/metier/tenant`, { headers, credentials: 'include' });
      if (tenantRes.ok) {
        setTenantInfo(await tenantRes.json());
      }

      // Charger client
      const res = await fetch(`${API}/api/metier/clients/${clientId}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setClient(data);
        setEditData(data);
      }

      // Charger dossiers du client
      const dossiersRes = await fetch(`${API}/api/metier/dossiers?client_id=${clientId}&limit=50`, { headers, credentials: 'include' });
      if (dossiersRes.ok) {
        setDossiers(await dossiersRes.json());
      }

      // Charger commentaires
      const commentsRes = await fetch(`${API}/api/metier/clients/${clientId}/commentaires`, { headers, credentials: 'include' });
      if (commentsRes.ok) {
        setCommentaires(await commentsRes.json());
      } else {
        // Si l'API n'existe pas encore, initialiser à vide
        setCommentaires([]);
      }
    } catch (err) {
      console.error("Erreur chargement client:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      };

      const res = await fetch(`${API}/api/metier/clients/${clientId}`, {
        method: "PATCH",
        headers,
        credentials: 'include',
        body: JSON.stringify(editData)
      });

      if (res.ok) {
        const updated = await res.json();
        setClient(updated);
        setEditing(false);
      }
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
    } finally {
      setSaving(false);
    }
  };

  // Relocation menu Clients (CC-DEV1) : l'archivage vit désormais sur la FICHE
  // client (était dans ProspectsList). Endpoints existants /archiver + /desarchiver.
  const handleToggleArchive = async () => {
    if (archiving || !client) return;
    const isArchived = client.statut === "archive" || client.statut === "archived" || client.archived;
    const action = isArchived ? "desarchiver" : "archiver";
    if (!isArchived && !window.confirm("Archiver ce client ? Il sera masqué des listes par défaut (réversible).")) return;
    setArchiving(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = {
        "X-Tenant-ID": tenantId || "climatpac",
        ...(token && { "Authorization": `Bearer ${token}` }),
      };
      const res = await fetch(`${API}/api/metier/clients/${clientId}/${action}`, {
        method: "POST",
        headers,
        credentials: "include",
      });
      if (res.ok) {
        toast.success(isArchived ? "Client désarchivé" : "Client archivé");
        await loadClient();
      } else {
        toast.error("Action impossible");
      }
    } catch (err) {
      console.error("Erreur archivage client:", err);
      toast.error("Erreur lors de l'archivage");
    } finally {
      setArchiving(false);
    }
  };

  const handleResyncZohoAll = async () => {
    if (syncingZoho) return;
    const tefDossiers = (dossiers || []).filter(
      d => (d.activite === "tef" || d.activite === "tef" || tenantId === "tef") && d.id
    );
    if (tefDossiers.length === 0) {
      toast.info("Aucun dossier TEF à synchroniser");
      return;
    }
    setSyncingZoho(true);
    let imp = 0, lnk = 0, skp = 0, ko = 0;
    try {
      const token = localStorage.getItem("session_token");
      const headers = {
        "X-Tenant-ID": tenantId || "tef",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      for (const d of tefDossiers) {
        try {
          const r = await fetch(
            `${API}/api/metier/tef/zoho/resync-dossier/${d.id}?push_to_zoho=false`,
            { method: "POST", headers, credentials: "include" }
          );
          const data = await r.json();
          if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
          const fz = data.from_zoho_documents || {};
          const fp = data.from_zoho_photos || {};
          imp += (fz.imported || 0) + (fp.imported || 0);
          lnk += (fz.linked || 0) + (fp.linked || 0);
          skp += (fz.skipped || 0) + (fp.skipped || 0);
        } catch (e) {
          ko += 1;
          console.error("[resync-zoho]", d.id, e);
        }
      }
      const msg = `Sync ${tefDossiers.length} dossier(s) — ${imp} importé(s), ${lnk} lié(s), ${skp} déjà à jour${ko ? `, ${ko} échec(s)` : ""}`;
      ko ? toast.warning(msg) : toast.success(msg);
      loadClient();
    } finally {
      setSyncingZoho(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      };
      
      // Récupérer le nom de l'utilisateur
      const auteurNom = currentUser?.nom 
        || currentUser?.name 
        || currentUser?.email?.split('@')[0] 
        || "Utilisateur";

      const res = await fetch(`${API}/api/metier/clients/${clientId}/commentaires`, {
        method: "POST",
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          contenu: newComment,
          auteur_nom: auteurNom
        })
      });

      if (res.ok) {
        const comment = await res.json();
        setCommentaires([comment, ...commentaires]);
        setNewComment("");
      }
    } catch (err) {
      console.error("Erreur ajout commentaire:", err);
    } finally {
      setAddingComment(false);
    }
  };

  // formatDate : timestamps avec heure (created_at, commentaires).
  // formatDateNaissance : date naissance pure JJ/MM/AAAA (ISO ou DDMMYYYY).
  const formatDate = formatDateTimeFR;
  const formatDateNaissance = (value) => {
    if (!value) return "-";
    const s = String(value).trim();
    // DDMMYYYY (8 chiffres legacy) → JJ/MM/AAAA
    const ddmm = s.match(/^(\d{2})(\d{2})(\d{4})$/);
    if (ddmm) return `${ddmm[1]}/${ddmm[2]}/${ddmm[3]}`;
    return formatDateFR(s);
  };

  // 2026-05-03 — Derive age depuis date_naissance ISO
  const computeAgeFromDateNaissance = (value) => {
    if (!value) return null;
    const s = String(value).trim();
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!isoMatch) return null;
    const birth = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
    if (Number.isNaN(birth.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
    return age;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-[var(--oh-error-text)] mb-4" />
        <p className="text-[var(--oh-text)]">Client non trouvé</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-[var(--oh-text-muted)]">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full ${estPersonneMorale(client.type_client) ? "bg-[var(--oh-accent-bg)]" : "bg-[var(--oh-accent-bg)]"} flex items-center justify-center`}>
              {estPersonneMorale(client.type_client) ? (
                <Building2 className="w-6 h-6 text-[var(--oh-accent-text)]" />
              ) : (
                <User className="w-6 h-6 text-[var(--oh-accent-text)]" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-medium text-[var(--oh-text)]" data-testid="client-header-name">
                {estPersonneMorale(client.type_client) ? (
                  <>
                    {client.raison_sociale || client.nom}
                    {client.nom_commercial && (
                      <span className="text-[var(--oh-text-muted)] font-normal ml-2">({client.nom_commercial})</span>
                    )}
                  </>
                ) : (
                  <>{formatClientFullName(client)}</>
                )}
              </h1>
              <p
                className="text-sm font-mono mt-0.5"
                style={{ color: client.reference ? "var(--oh-text)" : "var(--oh-text-muted)" }}
                data-testid="client-header-reference"
              >
                {client.reference
                  ? `${client.reference} - ${
                      estPersonneMorale(client.type_client)
                        ? (client.raison_sociale || client.nom || "").toUpperCase()
                        : formatClientFullName(client).toUpperCase()
                    }`
                  : "Sans référence"}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <Badge className={estPersonneMorale(client.type_client) ? "bg-[var(--oh-accent-solid)]" : "bg-[var(--oh-bg-elevated)]"}>
                  {libelleType(client.type_client)}
                </Badge>
                {client.siren && (
                  <Badge className="bg-[var(--oh-bg-elevated)] font-mono text-xs">SIREN: {client.siren}</Badge>
                )}
                {client.statut === "archive" && (
                  <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]">Archivé</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2 pl-12 sm:pl-0">
          {/* Resync Zoho — disponible si tenant TEF et au moins 1 dossier */}
          {(tenantId === "tef" || tenantInfo?.activites_disponibles?.tef) && dossiers.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-[var(--oh-border)]"
              style={{ color: "var(--oh-text-muted)" }}
              onClick={handleResyncZohoAll}
              disabled={syncingZoho}
              title="Resynchroniser les fichiers Zoho de tous les dossiers du client"
              data-testid="resync-zoho-client-btn"
              aria-label="Resync Zoho"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${syncingZoho ? "animate-spin" : ""}`} />
              Resync Zoho
            </Button>
          )}
          {/* Boutons création dossier */}
          {tenantInfo?.activites_disponibles?.tef && (
            <Button
              variant="outline"
              size="sm"
              className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]"
              onClick={() => onCreateDossier?.(clientId, "tef")}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Dossier TEF
            </Button>
          )}
          {tenantInfo?.activites_disponibles?.climat && (
            <Button
              variant="outline"
              size="sm"
              className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
              onClick={() => onCreateDossier?.(clientId, "climat")}
            >
              <Thermometer className="w-4 h-4 mr-1" />
              Dossier Climat
            </Button>
          )}
          {/* Archivage client — relogé depuis le menu Clients (CC-DEV1) */}
          <Button
            variant="outline"
            size="sm"
            className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
            onClick={handleToggleArchive}
            disabled={archiving}
            data-testid="client-archive-btn"
            aria-label={(client.statut === "archive" || client.archived) ? "Désarchiver le client" : "Archiver le client"}
          >
            <Archive className={`w-4 h-4 mr-1 ${archiving ? "animate-pulse" : ""}`} />
            {(client.statut === "archive" || client.statut === "archived" || client.archived) ? "Désarchiver" : "Archiver"}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] p-1 flex overflow-x-auto no-scrollbar">
          <TabsTrigger value="infos" className="data-[state=active]:bg-[var(--oh-bg-elevated)] whitespace-nowrap text-xs sm:text-sm">
            <User className="w-4 h-4 mr-1" /> Informations
          </TabsTrigger>
          <TabsTrigger value="dossiers" className="data-[state=active]:bg-[var(--oh-bg-elevated)] whitespace-nowrap text-xs sm:text-sm">
            <FileText className="w-4 h-4 mr-1" /> Dossiers
            {(() => {
              const activeDossiers = dossiers.filter(d => 
                d.statut !== "archive" && d.statut !== "archived" && !d.archived
              );
              return activeDossiers.length > 0 && (
                <Badge className="ml-1 bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] text-xs">{activeDossiers.length}</Badge>
              );
            })()}
          </TabsTrigger>
          <TabsTrigger value="commentaires" className="data-[state=active]:bg-[var(--oh-bg-elevated)] whitespace-nowrap text-xs sm:text-sm">
            <MessageSquare className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Commentaires</span><span className="sm:hidden">Notes</span>
            {commentaires.length > 0 && (
              <Badge className="ml-1 bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] text-xs">{commentaires.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Onglet Informations */}
        <TabsContent value="infos" className="mt-4">
          <OhCard style={{ padding: "var(--oh-space-6)" }}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[var(--oh-text)] font-medium">Informations client</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editing ? handleSave() : setEditing(true)}
                disabled={saving}
                aria-label={editing ? "Enregistrer le client" : "Modifier le client"}
                data-testid="client-edit-toggle-btn"
              >
                {editing ? (
                  saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />
                ) : (
                  <Edit className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Type de client - Toujours visible */}
              <div>
                <p className="text-[var(--oh-text-muted)] text-xs mb-1">Type</p>
                {editing ? (
                  <Select
                    value={editData.type_client || "particulier"}
                    onValueChange={(v) => setEditData({...editData, type_client: v})}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="particulier">Particulier</SelectItem>
                      <SelectItem value="professionnel">Professionnel / Entreprise</SelectItem>
                      <SelectItem value="copropriete">Copropriété</SelectItem>
                      <SelectItem value="bailleur">Bailleur</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={estPersonneMorale(client.type_client) ? "bg-[var(--oh-accent-solid)]" : "bg-[var(--oh-bg-elevated)]"}>
                    {libelleType(client.type_client)}
                  </Badge>
                )}
              </div>

              {/* === SECTION ENTREPRISE === */}
              {(editing ? estPersonneMorale(editData.type_client) : estPersonneMorale(client.type_client)) ? (
                <>
                  {/* Raison Sociale */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Raison sociale *</p>
                    {editing ? (
                      <Input
                        value={editData.raison_sociale || editData.nom || ""}
                        onChange={(e) => setEditData({...editData, raison_sociale: e.target.value, nom: e.target.value})}
                        onBlur={(e) => { const v = normalizeName(e.target.value, "entreprise"); setEditData({...editData, raison_sociale: v, nom: v}); }}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="OSTRYA"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">{client.raison_sociale || client.nom}</p>
                    )}
                  </div>
                  
                  {/* Nom Commercial */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Nom commercial</p>
                    {editing ? (
                      <Input
                        value={editData.nom_commercial || ""}
                        onChange={(e) => setEditData({...editData, nom_commercial: e.target.value})}
                        onBlur={(e) => setEditData({...editData, nom_commercial: normalizeName(e.target.value, "entreprise")})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="OBJECTIF PAC"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)]">{client.nom_commercial || "-"}</p>
                    )}
                  </div>
                  
                  {/* SIREN */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">SIREN</p>
                    {editing ? (
                      <Input
                        value={editData.siren || ""}
                        onChange={(e) => setEditData({...editData, siren: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="890517782"
                        maxLength={9}
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-mono">{client.siren || "-"}</p>
                    )}
                  </div>
                  
                  {/* SIRET */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">SIRET</p>
                    {editing ? (
                      <Input
                        value={editData.siret || ""}
                        onChange={(e) => setEditData({...editData, siret: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="00000000000000"
                        maxLength={14}
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-mono">{client.siret || "-"}</p>
                    )}
                  </div>
                  
                  {/* TVA Intracommunautaire */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">N° TVA Intracommunautaire</p>
                    {editing ? (
                      <Input
                        value={editData.tva_intracom || ""}
                        onChange={(e) => setEditData({...editData, tva_intracom: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="FR19890517782"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-mono">{client.tva_intracom || "-"}</p>
                    )}
                  </div>
                  
                  {/* Forme Juridique */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Forme juridique</p>
                    {editing ? (
                      <Select
                        value={editData.forme_juridique || ""}
                        onValueChange={(v) => setEditData({...editData, forme_juridique: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SARL">SARL</SelectItem>
                          <SelectItem value="SAS">SAS</SelectItem>
                          <SelectItem value="SASU">SASU</SelectItem>
                          <SelectItem value="EURL">EURL</SelectItem>
                          <SelectItem value="SA">SA</SelectItem>
                          <SelectItem value="EI">Entreprise Individuelle</SelectItem>
                          <SelectItem value="AUTO">Auto-entrepreneur</SelectItem>
                          <SelectItem value="SCI">SCI</SelectItem>
                          <SelectItem value="AUTRE">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[var(--oh-text)]">{client.forme_juridique || "-"}</p>
                    )}
                  </div>
                  
                  {/* Section Contact Principal */}
                  <div className="col-span-full mt-4 pt-4 border-t border-[var(--oh-border)]">
                    <h4 className="text-[var(--oh-text-muted)] font-medium mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Contact principal
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <p className="text-[var(--oh-text-muted)] text-xs mb-1">Civilité</p>
                        {editing ? (
                          <Select
                            value={editData.contact_civilite || ""}
                            onValueChange={(v) => setEditData({...editData, contact_civilite: v})}
                          >
                            <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                              <SelectValue placeholder="Civilité" />
                            </SelectTrigger>
                            <SelectContent>
                              {civiliteOptions.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-[var(--oh-text)]">{client.contact_civilite || "-"}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-[var(--oh-text-muted)] text-xs mb-1">Nom</p>
                        {editing ? (
                          <Input
                            value={editData.contact_nom || ""}
                            onChange={(e) => setEditData({...editData, contact_nom: e.target.value})}
                            onBlur={(e) => setEditData({...editData, contact_nom: normalizeName(e.target.value, "nom")})}
                            className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                          />
                        ) : (
                          <p className="text-[var(--oh-text)]">{client.contact_nom || "-"}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-[var(--oh-text-muted)] text-xs mb-1">Prénom</p>
                        {editing ? (
                          <Input
                            value={editData.contact_prenom || ""}
                            onChange={(e) => setEditData({...editData, contact_prenom: e.target.value})}
                            onBlur={(e) => setEditData({...editData, contact_prenom: normalizeName(e.target.value, "prenom")})}
                            className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                          />
                        ) : (
                          <p className="text-[var(--oh-text)]">{client.contact_prenom || "-"}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* === SECTION PARTICULIER === */
                <>
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Civilité</p>
                    {editing ? (
                      <Select
                        value={editData.civilite || ""}
                        onValueChange={(v) => setEditData({...editData, civilite: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Civilité" />
                        </SelectTrigger>
                        <SelectContent>
                          {civiliteOptions.length > 0
                            ? civiliteOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)
                            : <><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem><SelectItem value="M. et Mme">M. et Mme</SelectItem></>
                          }
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[var(--oh-text)]">{client.civilite || "-"}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Nom</p>
                    {editing ? (
                      <Input
                        value={editData.nom || ""}
                        onChange={(e) => setEditData({...editData, nom: e.target.value})}
                        onBlur={(e) => setEditData({...editData, nom: normalizeName(e.target.value, "nom")})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">{client.nom}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Prénom</p>
                    {editing ? (
                      <Input
                        value={editData.prenom || ""}
                        onChange={(e) => setEditData({...editData, prenom: e.target.value})}
                        onBlur={(e) => setEditData({...editData, prenom: normalizeName(e.target.value, "prenom")})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)]">{client.prenom || "-"}</p>
                    )}
                  </div>
                </>
              )}
              
              {/* === CHAMPS COMMUNS (Email, Téléphone) === */}
              <div>
                <p className="text-[var(--oh-text-muted)] text-xs mb-1">Email</p>
                {editing ? (
                  <Input
                    type="email"
                    value={editData.email || ""}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                    <p className="text-[var(--oh-text)]">{client.email || "-"}</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-[var(--oh-text-muted)] text-xs mb-1">Téléphone</p>
                {editing ? (
                  <Input
                    value={editData.telephone || ""}
                    onChange={(e) => setEditData({...editData, telephone: e.target.value})}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                    <p className="text-[var(--oh-text)]">{client.telephone || "-"}</p>
                  </div>
                )}
              </div>

              {/* CC-CONTACT2 — Contact secondaire (donnée client, identique au bloc CLIENT du dossier) */}
              <div className="col-span-full">
                <p className="text-[var(--oh-text-muted)] text-xs mb-1 mt-2 font-medium">
                  Contact secondaire
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ["contact_secondaire_nom", "Nom"],
                    ["contact_secondaire_prenom", "Prénom"],
                    ["contact_secondaire_telephone", "Téléphone"],
                    ["contact_secondaire_relation", "Lien"],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <p className="text-[var(--oh-text-muted)] text-xs mb-1">{label}</p>
                      {editing ? (
                        key === "contact_secondaire_relation" ? (
                          <OhSelect
                            value={editData[key] || ""}
                            onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                            data-testid={`client-${key}`}
                          >
                            <option value="">-</option>
                            {CONTACT_RELATION_VALUES.map((v) => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </OhSelect>
                        ) : (
                          <Input
                            value={editData[key] || ""}
                            onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
                            className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                            data-testid={`client-${key}`}
                          />
                        )
                      ) : (
                        <p className="text-[var(--oh-text)]">{client[key] || "-"}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Adresse */}
              <div className="col-span-full">
                <p className="text-[var(--oh-text-muted)] text-xs mb-1">Adresse</p>
                {editing ? (
                  <div className="space-y-2">
                    <AdresseAutocomplete
                      placeholder="Rechercher une adresse (BAN)..."
                      onSelect={(addr) => setEditData({
                        ...editData,
                        adresse: {
                          ...(editData.adresse || {}),
                          rue: [addr.numero, addr.rue].filter(Boolean).join(" ").trim(),
                          code_postal: addr.code_postal || "",
                          ville: addr.ville || "",
                        },
                      })}
                    />
                    <Input
                      placeholder="Rue"
                      value={editData.adresse?.rue || ""}
                      onChange={(e) => setEditData({...editData, adresse: {...(editData.adresse || {}), rue: e.target.value}})}
                      onBlur={(e) => setEditData({...editData, adresse: {...(editData.adresse || {}), rue: normalizeName(e.target.value, "adresse")}})}
                      className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input
                        placeholder="Code postal"
                        value={editData.adresse?.code_postal || ""}
                        onChange={(e) => setEditData({...editData, adresse: {...(editData.adresse || {}), code_postal: e.target.value}})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                      />
                      <Input
                        placeholder="Ville"
                        value={editData.adresse?.ville || ""}
                        onChange={(e) => setEditData({...editData, adresse: {...(editData.adresse || {}), ville: e.target.value}})}
                        onBlur={(e) => setEditData({...editData, adresse: {...(editData.adresse || {}), ville: normalizeName(e.target.value, "ville")}})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                      />
                    </div>
                  </div>
                ) : (() => {
                  // Fix [object Object] : adresse peut être dict ou string.
                  // formatClientAddress aplatit en string canonique
                  // "rue, code_postal ville". On split sur ", " pour
                  // garder l'affichage 2-lignes historique (rue / CP ville).
                  const formatted = formatClientAddress(client);
                  if (!formatted) {
                    return <p className="text-[var(--oh-text-muted)]">Non renseignée</p>;
                  }
                  const parts = formatted.split(", ");
                  return (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-[var(--oh-text-secondary)] mt-0.5" />
                      <p className="text-[var(--oh-text)]">
                        {parts[0]}{parts.length > 1 ? <><br />{parts.slice(1).join(", ")}</> : null}
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Section Fiscale - RFR, Nb personnes, Région, Couleur MPR - CLIMAT only */}
              {tenantId !== "tef" && (
              <div className="col-span-full mt-4 pt-4 border-t border-[var(--oh-border)]">
                <h4 className="text-[var(--oh-text)] font-medium mb-4 flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[var(--oh-success-text)]" />
                  Informations fiscales & éligibilité MPR
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Revenu Fiscal de Référence */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Revenu Fiscal de Référence (RFR)</p>
                    {editing ? (
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                        <Input
                          type="number"
                          value={editData.rfr || ""}
                          onChange={(e) => setEditData({...editData, rfr: e.target.value ? parseFloat(e.target.value) : null})}
                          className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] pl-9"
                          placeholder="25000"
                        />
                      </div>
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">
                        {client.rfr ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(client.rfr) : "-"}
                      </p>
                    )}
                  </div>
                  
                  {/* Nombre de personnes dans le foyer */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Personnes dans le foyer fiscal</p>
                    {editing ? (
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--oh-text-secondary)]" />
                        <Input
                          type="number"
                          min="1"
                          max="20"
                          value={editData.nb_personnes_foyer || ""}
                          onChange={(e) => setEditData({...editData, nb_personnes_foyer: e.target.value ? parseInt(e.target.value) : null})}
                          className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] pl-9"
                          placeholder="2"
                        />
                      </div>
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">
                        {client.nb_personnes_foyer ? `${client.nb_personnes_foyer} personne(s)` : "-"}
                      </p>
                    )}
                  </div>
                  
                  {/* Région */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Région</p>
                    {editing ? (
                      <Select
                        value={editData.region_fiscale || ""}
                        onValueChange={(v) => setEditData({...editData, region_fiscale: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="idf">Île-de-France</SelectItem>
                          <SelectItem value="province">Province (hors IDF)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[var(--oh-text)]">
                        {client.region_fiscale === "idf" ? "Île-de-France" : 
                         client.region_fiscale === "province" ? "Province" : "-"}
                      </p>
                    )}
                  </div>
                  
                  {/* Couleur MPR calculée */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Couleur MaPrimeRénov'</p>
                    {(() => {
                      const couleurMPR = calculerCouleurMPR(
                        editing ? editData.rfr : client.rfr,
                        editing ? editData.nb_personnes_foyer : client.nb_personnes_foyer,
                        editing ? editData.region_fiscale : client.region_fiscale
                      );
                      
                      if (!couleurMPR) {
                        return <p className="text-[var(--oh-text-secondary)] italic">Renseignez RFR, nb personnes et région</p>;
                      }
                      
                      return (
                        <div className="flex items-center gap-2">
                          <Badge className="px-3 py-1 border" style={couleurMPR.badgeStyle}>
                            {capitalizeFR(couleurMPR.couleur)}
                          </Badge>
                          <span className="text-sm" style={couleurMPR.labelStyle}>
                            {couleurMPR.label}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Aide visuelle des barèmes */}
                {!editing && client.rfr && client.nb_personnes_foyer && client.region_fiscale && (
                  <div className="mt-4 p-3 bg-[var(--oh-bg-elevated)] rounded-lg">
                    <p className="text-xs text-[var(--oh-text-muted)] mb-2">Barème MPR {client.region_fiscale === "idf" ? "Île-de-France" : "Province"} pour {client.nb_personnes_foyer} personne(s) :</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {(() => {
                        const bareme = client.region_fiscale === "idf" ? BAREMES_MPR.idf : BAREMES_MPR.province;
                        const nb = Math.min(client.nb_personnes_foyer, 5);
                        const seuils = bareme[nb];
                        return (
                          <>
                            <span className="text-[var(--oh-success-text)]">Bleu: ≤ {seuils.bleu.toLocaleString()}€</span>
                            <span className="text-[var(--oh-info-text)]">Jaune: ≤ {seuils.jaune.toLocaleString()}€</span>
                            <span className="text-[var(--oh-accent-text)]">Violet: ≤ {seuils.violet.toLocaleString()}€</span>
                            <span className="text-[var(--oh-error-text)]">Rose: &gt; {seuils.violet.toLocaleString()}€</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Section MaPrimeAdapt' - Champs spécifiques MPA - TEF only */}
              {tenantId === "tef" && (
              <div className="col-span-full mt-4 pt-4 border-t border-[var(--oh-border)]" data-testid="section-mpa">
                <h4 className="text-[var(--oh-text)] font-medium mb-4 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[var(--oh-success-text)]" />
                  MaPrimeAdapt' (MPA)
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Age */}
                  <div data-testid="client-edit-age">
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Age du bénéficiaire</p>
                    {editing ? (
                      <Input
                        type="number"
                        min="0"
                        max="120"
                        value={editData.age || ""}
                        onChange={(e) => setEditData({...editData, age: e.target.value ? parseInt(e.target.value) : null})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="65"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">{client.age ? `${client.age} ans` : "-"}</p>
                    )}
                  </div>
                  
                  {/* GIR */}
                  <div data-testid="client-edit-gir">
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">GIR (Groupe Iso-Ressources)</p>
                    {editing ? (
                      <Select
                        value={editData.gir || "_none"}
                        onValueChange={(v) => setEditData({...editData, gir: v === "_none" ? "" : v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Non renseigné</SelectItem>
                          <SelectItem value="gir_1">GIR 1</SelectItem>
                          <SelectItem value="gir_2">GIR 2</SelectItem>
                          <SelectItem value="gir_3">GIR 3</SelectItem>
                          <SelectItem value="gir_4">GIR 4</SelectItem>
                          <SelectItem value="gir_5">GIR 5</SelectItem>
                          <SelectItem value="gir_6">GIR 6</SelectItem>
                          <SelectItem value="non_evalue">Non évalué</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (() => {
                      // 2026-05-03 — GIR "Non requis" si age >= 70 et pas de GIR
                      if (client.gir) {
                        return (
                          <p className="text-[var(--oh-text)] font-medium">
                            {client.gir.replace("gir_", "GIR ").replace("non_evalue", "Non évalué")}
                          </p>
                        );
                      }
                      const age = client.age ?? computeAgeFromDateNaissance(client.date_naissance);
                      if (age !== null && age >= 70) {
                        return (
                          <p className="text-[var(--oh-text-secondary)] italic" title="Eligibilite MPA acquise par age">
                            Non requis
                          </p>
                        );
                      }
                      return <p className="text-[var(--oh-text)] font-medium">-</p>;
                    })()}
                  </div>
                  
                  {/* Bénéficiaire APA */}
                  <div data-testid="client-edit-apa">
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Bénéficiaire APA</p>
                    {editing ? (
                      <Select
                        value={editData.beneficiaire_apa === true ? "true" : editData.beneficiaire_apa === false ? "false" : "_none"}
                        onValueChange={(v) => setEditData({...editData, beneficiaire_apa: v === "true" ? true : v === "false" ? false : null})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Non renseigné</SelectItem>
                          <SelectItem value="true">Oui</SelectItem>
                          <SelectItem value="false">Non</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={client.beneficiaire_apa === true ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : client.beneficiaire_apa === false ? "bg-[var(--oh-bg-surface)] text-[var(--oh-text-secondary)] border border-[var(--oh-border)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)]"}>
                        {client.beneficiaire_apa === true ? "Oui" : client.beneficiaire_apa === false ? "Non" : "Non renseigné"}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Bénéficiaire PCH */}
                  <div data-testid="client-edit-pch">
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Bénéficiaire PCH</p>
                    {editing ? (
                      <Select
                        value={editData.beneficiaire_pch === true ? "true" : editData.beneficiaire_pch === false ? "false" : "_none"}
                        onValueChange={(v) => setEditData({...editData, beneficiaire_pch: v === "true" ? true : v === "false" ? false : null})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Non renseigné</SelectItem>
                          <SelectItem value="true">Oui</SelectItem>
                          <SelectItem value="false">Non</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={client.beneficiaire_pch === true ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : client.beneficiaire_pch === false ? "bg-[var(--oh-bg-surface)] text-[var(--oh-text-secondary)] border border-[var(--oh-border)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)]"}>
                        {client.beneficiaire_pch === true ? "Oui" : client.beneficiaire_pch === false ? "Non" : "Non renseigné"}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Taux Handicap */}
                  <div data-testid="client-edit-handicap">
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Taux handicap (%)</p>
                    {editing ? (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editData.taux_handicap || ""}
                        onChange={(e) => setEditData({...editData, taux_handicap: e.target.value ? parseInt(e.target.value) : null})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="80"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">{client.taux_handicap ? `${client.taux_handicap}%` : "-"}</p>
                    )}
                  </div>
                  
                  {/* Date naissance */}
                  <div data-testid="client-edit-dob">
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Date de naissance</p>
                    {editing ? (
                      <Input
                        type="text"
                        value={editData.date_naissance || ""}
                        onChange={(e) => setEditData({...editData, date_naissance: e.target.value})}
                        className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
                        placeholder="JJ/MM/AAAA"
                      />
                    ) : (
                      <p className="text-[var(--oh-text)]">{formatDateNaissance(client.date_naissance)}</p>
                    )}
                  </div>
                  
                  {/* Zone précarité */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Zone de précarité</p>
                    {editing ? (
                      <Select
                        value={editData.zone_precarite || "_none"}
                        onValueChange={(v) => setEditData({...editData, zone_precarite: v === "_none" ? "" : v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Non renseigné</SelectItem>
                          <SelectItem value="tres_modestes">Très modestes</SelectItem>
                          <SelectItem value="modestes">Modestes</SelectItem>
                          <SelectItem value="intermediaires">Intermédiaires</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[var(--oh-text)]">
                        {client.zone_precarite === "tres_modestes" ? "Très modestes" : 
                         client.zone_precarite === "modestes" ? "Modestes" : 
                         client.zone_precarite === "intermediaires" ? "Intermédiaires" : "-"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* Section Situation Logement */}
              <div className="col-span-full mt-4 pt-4 border-t border-[var(--oh-border)]">
                <h4 className="text-[var(--oh-text)] font-medium mb-4 flex items-center gap-2">
                  <Home className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                  Situation logement
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Propriétaire / Locataire */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Statut d'occupation</p>
                    {editing ? (
                      <Select
                        value={editData.statut_occupation || ""}
                        onValueChange={(v) => setEditData({...editData, statut_occupation: v})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {occupationOptions.map(o => (
                            <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[var(--oh-text)] font-medium">
                        {occupationOptions.find(o => o.id === client.statut_occupation)?.label || client.statut_occupation || "-"}
                      </p>
                    )}
                  </div>
                  
                  {/* Résidence Principale
                      🔴 #546 (DETTE-120, invention n°2) — le badge testait
                      `!== false`. Or `undefined !== false` est VRAI : les 216
                      clients preprod SANS la donnée affichaient « Oui ». Aucun
                      rendu n'existait pour « inconnu ». L'écran était catégorique
                      là où la base était muette.
                      ⛔ L'éditeur est retiré d'ici : la résidence principale
                      décrit un LOGEMENT, donc un DOSSIER — et 4 clients prod ont
                      des dossiers sur des ADRESSES DIFFÉRENTES, qu'une valeur
                      unique au niveau client ne peut pas décrire. La saisie a
                      lieu sur la fiche dossier, seule source que le calculateur
                      d'éligibilité consomme. Ce qui reste ici est une valeur
                      HISTORIQUE, en lecture seule, et annoncée comme telle. */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Résidence principale</p>
                    <Badge
                      style={
                        client.residence_principale === true
                          ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }
                          : client.residence_principale === false
                          ? { background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }
                          : { background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }
                      }
                    >
                      {client.residence_principale === true
                        ? "Oui (historique)"
                        : client.residence_principale === false
                        ? "Non (historique)"
                        : "Indéterminé"}
                    </Badge>
                    <p className="text-[var(--oh-text-muted)] text-[10px] mt-1">
                      Se saisit sur la fiche dossier — elle décrit le logement concerné.
                    </p>
                  </div>
                  
                  {/* Indivision */}
                  <div>
                    <p className="text-[var(--oh-text-muted)] text-xs mb-1">Indivision</p>
                    {editing ? (
                      <Select
                        value={editData.indivision === true ? "oui" : "non"}
                        onValueChange={(v) => setEditData({...editData, indivision: v === "oui"})}
                      >
                        <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="non">Non</SelectItem>
                          <SelectItem value="oui">Oui</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Badge className={client.indivision === true ? "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)]" : "bg-[var(--oh-bg-surface)] text-[var(--oh-text-secondary)] border border-[var(--oh-border)]"}>
                          {client.indivision === true ? "Oui" : "Non"}
                        </Badge>
                        {client.indivision === true && (
                          <span className="text-xs text-[var(--oh-info-text)]">(Document d'indivision requis)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Alerte indivision — encart d'état normé (dual-mode, contraste garanti) */}
                {!editing && client.indivision === true && (
                  <OhStatusNote
                    variant="info"
                    title="Bien en indivision"
                    className="mt-4"
                    data-testid="indivision-alert"
                  >
                    Un document d'autorisation des co-indivisaires avec signature électronique est requis avant de poursuivre le dossier.
                  </OhStatusNote>
                )}
              </div>
            </div>

            {editing && (
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[var(--oh-border)]">
                <Button variant="outline" onClick={() => { setEditing(false); setEditData(client); }} data-testid="client-cancel-btn">
                  <X className="w-4 h-4 mr-1" />
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] hover:bg-[var(--oh-accent-solid)]" data-testid="client-save-btn">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Enregistrer le client
                </Button>
              </div>
            )}
          </OhCard>
        </TabsContent>

        {/* Onglet Dossiers */}
        <TabsContent value="dossiers" className="mt-4">
          {/* 🔴 L2 (N°876) — CLIENT 1─N SITE 1─N DOSSIER : les sites du client et,
              sous chacun, ses dossiers. Seulement pour un tenant qui a ADOPTÉ le
              modèle Site ⇒ TEF, OMV, Climat : ⛔ inchangés. */}
          {hasDossierSite() && (
            <div className="mb-4">
              <SitesDuClient clientId={clientId}
                             onOpenDossier={(id) => onNavigate?.("dossier", { id })} />
            </div>
          )}
          <OhCard style={{ padding: "var(--oh-space-6)" }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div className="flex items-center gap-4">
                <h3 className="text-[var(--oh-text)] font-medium">Dossiers du client</h3>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="showArchivedDossiers"
                    checked={showArchivedDossiers}
                    onCheckedChange={setShowArchivedDossiers}
                    className="border-[var(--oh-border)]"
                  />
                  <label 
                    htmlFor="showArchivedDossiers" 
                    className="text-sm text-[var(--oh-text-muted)] cursor-pointer flex items-center gap-1"
                  >
                    <Archive className="w-3 h-3" />
                    Afficher archivés
                  </label>
                </div>
              </div>
              <Button
                className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] hover:bg-[var(--oh-accent-solid)] w-full sm:w-auto"
                onClick={() => onCreateDossier?.(clientId)}
                data-testid="create-dossier-btn"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Créer un dossier
              </Button>
            </div>
            
            {/* Filtrer les dossiers selon le toggle archivés */}
            {(() => {
              const filteredDossiers = dossiers.filter(d => {
                if (!showArchivedDossiers) {
                  // Masquer les archivés
                  return d.statut !== "archive" && d.statut !== "archived" && !d.archived;
                }
                return true;
              });
              
              const archivedCount = dossiers.filter(d => 
                d.statut === "archive" || d.statut === "archived" || d.archived
              ).length;
              
              return filteredDossiers.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-3" />
                  <p className="text-[var(--oh-text-muted)]">
                    {dossiers.length === 0 
                      ? "Aucun dossier pour ce client"
                      : `Aucun dossier actif (${archivedCount} archivé${archivedCount > 1 ? 's' : ''})`
                    }
                  </p>
                  {archivedCount > 0 && !showArchivedDossiers && (
                    <Button
                      variant="link"
                      onClick={() => setShowArchivedDossiers(true)}
                      className="text-[var(--oh-accent-text)] text-sm mt-2"
                      data-testid="show-archived-dossiers-btn"
                    >
                      Afficher les dossiers archivés
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredDossiers.map((dossier) => (
                    <div
                      key={dossier.id}
                      className={`flex items-center gap-4 p-4 rounded-lg cursor-pointer transition-colors ${
                        dossier.statut === "archive" || dossier.archived 
                          ? "bg-[var(--oh-bg-elevated)] opacity-60 hover:opacity-80" 
                          : "bg-[var(--oh-bg-elevated)] hover:bg-[var(--oh-bg-elevated)]"
                      }`}
                      onClick={() => onNavigate?.("dossier", { id: dossier.id })}
                    >
                      <div className={`p-2 rounded-lg ${dossier.activite === "tef" ? "bg-[var(--oh-accent-bg)]" : "bg-[var(--oh-info-bg)]"}`}>
                        {dossier.activite === "tef" ? (
                          <Building2 className="w-5 h-5 text-[var(--oh-accent-text)]" />
                        ) : (
                          <Thermometer className="w-5 h-5 text-[var(--oh-info-text)]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-[var(--oh-text)] font-medium">{dossier.référence}</p>
                        <p className="text-[var(--oh-text-muted)] text-sm">{formatDate(dossier.created_at)}</p>
                      </div>
                      <Badge className={
                        dossier.statut === "archive" || dossier.archived
                          ? "bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] text-[var(--oh-text-muted)]"
                          : "bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] text-[var(--oh-text-secondary)]"
                      }>
                        {dossier.statut}
                      </Badge>
                    </div>
                  ))}
                </div>
              );
            })()}
          </OhCard>
        </TabsContent>

        {/* Onglet Commentaires */}
        <TabsContent value="commentaires" className="mt-4">
          <OhCard style={{ padding: "var(--oh-space-6)" }}>
            <h3 className="text-[var(--oh-text)] font-medium mb-4">Commentaires</h3>
            
            {/* Formulaire ajout commentaire */}
            <div className="flex gap-2 mb-6">
              <Textarea
                placeholder="Ajouter un commentaire..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] min-h-[80px] flex-1"
              />
              <Button
                onClick={handleAddComment}
                disabled={!newComment.trim() || addingComment}
                className="bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)] hover:bg-[var(--oh-accent-solid)] self-end"
                aria-label="Envoyer le commentaire"
                data-testid="add-comment-btn"
              >
                {addingComment ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* Liste des commentaires */}
            {commentaires.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-3" />
                <p className="text-[var(--oh-text-muted)]">Aucun commentaire</p>
                <p className="text-[var(--oh-text-secondary)] text-sm mt-1">Ajoutez des notes sur ce client</p>
              </div>
            ) : (
              <div className="space-y-4">
                {commentaires.map((comment, idx) => (
                  <div key={comment.id || idx} className="p-4 bg-[var(--oh-bg-elevated)] rounded-lg">
                    <p className="text-[var(--oh-text)] whitespace-pre-wrap">{comment.contenu}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-[var(--oh-text-muted)]">
                      <span>{comment.auteur_nom || "Système"}</span>
                      <span>•</span>
                      <span>{formatDate(comment.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </OhCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
